import arrayMove from "array-move";
import { produce } from "immer";
import find from "lodash/find";
import React, { useContext, useEffect } from "react";
import styled from "styled-components";

import { Button, RichContentDisplay } from "@examspark/common-ui";
import {
  IconAddLine,
  IconRemoveCircleLine,
} from "@examspark/common-ui/dist/components/icons";
import { StemOrder } from "@examspark/types";

import Mark from "../common/Mark";
import QuestionContext from "../QuestionContext";

import ChoiceDragHandle from "./common/ChoiceDragHandle";
import SortableRows from "./common/SortableRows";
import Prompt from "./shared/Prompt";

const ResponseArea = styled.div`
  border: dashed 2px #ddd;
  padding: 0 0.3rem;
`;

const EmptyResponse = styled.div`
  font-size: 1em;
  text-align: center;
  color: #888;
  padding: 1.5em;
`;

const Choice = styled.div`
  background-color: #f2f2f2;
  margin: 0.2rem 0;
  display: flex;
  padding: 1rem;
  position: relative;

  button {
    background-color: white;
  }

  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const Selection = styled.div`
  background-color: #f2f2f2;
  margin: 0.2rem 0;
  display: flex;
  padding: 1rem 0.5rem;
  position: relative;
  display: flex;
  gap: 1rem;

  button {
    background-color: white;
  }

  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const ChoiceContent = styled.div`
  align-self: center;
  flex: 1;
`;

const Sequence = (props: { data: StemOrder }) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            order: props.data.scoring?.order,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.revealKey]);

  const handleSequencedOptionAdd = (optId: string) => {
    const rsp = produce(
      player?.response?.[props.data.id] || {},
      (draft: any) => {
        if (!draft.order) {
          draft.order = [];
        }
        draft.order.push(optId);
      },
    );

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  const handleSequencedOptionRemove = (idx: number) => {
    const rsp = produce(player?.response?.[props.data.id], (draft: any) => {
      draft.order.splice(idx, 1);
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  const handleSort = (oldIndex: any, newIndex: any) => {
    const rsp = produce(player?.response?.[props.data.id], (draft: any) => {
      const newOrder = arrayMove(draft.order, oldIndex, newIndex);
      draft.order = newOrder;
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  return (
    <div className="item-player-stem-sequence">
      <Prompt data={props.data.content.prompt} />
      <ResponseArea>
        {!player?.response?.[props.data.id]?.order?.length && (
          <EmptyResponse>No Items</EmptyResponse>
        )}

        {player?.response?.[props.data.id]?.order && (
          <SortableRows
            lockAxis="y"
            lockToContainerEdges
            onSort={handleSort}
            useDragHandle
          >
            {player.response?.[props.data.id].order.map(
              (optId: string, idx: number) => {
                const opt = find(props.data.content.options, ["id", optId]);

                let mark: "correct" | "incorrect" | undefined;
                if (player.scoreResponses) {
                  mark = "incorrect";
                  const key = props?.data?.scoring?.order || [];

                  if (key[idx] === optId) {
                    mark = "correct";
                  }
                }

                return (
                  <Selection key={`${optId}.${idx}`}>
                    <ChoiceDragHandle />
                    <ChoiceContent>
                      <RichContentDisplay content={opt?.body} />
                    </ChoiceContent>
                    <div>
                      <Button
                        title="Remove Option"
                        onClick={() => {
                          handleSequencedOptionRemove(idx);
                        }}
                        icon={<IconRemoveCircleLine />}
                      >
                        Remove
                      </Button>
                    </div>
                    {!!mark && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "0 1rem",
                        }}
                      >
                        {mark && <Mark type={mark} />}
                      </div>
                    )}
                  </Selection>
                );
              },
            )}
          </SortableRows>
        )}
      </ResponseArea>
      <div className="item-player-stem-sequence-options">
        {props.data.content.options.map((opt, idx) => {
          return (
            <Choice key={idx}>
              <ChoiceContent>
                <RichContentDisplay content={opt.body} />
              </ChoiceContent>
              <div>
                <Button
                  title="Add Option"
                  onClick={() => {
                    handleSequencedOptionAdd(opt.id);
                  }}
                  icon={<IconAddLine />}
                >
                  Select
                </Button>
              </div>
            </Choice>
          );
        })}
      </div>
    </div>
  );
};
export default Sequence;
